import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, message, Popconfirm, Tooltip, Alert } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { adminService } from '../../services/api';
import ProfileForm from './ProfileForm';
import { useAuth } from '../../context/AuthContext'; 

const ProfilesTab = () => {
    const { user } = useAuth();
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProfile, setEditingProfile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deleteError, setDeleteError] = useState(null); 

    const canCreateProfile = user?.role?.profile?.permissions?.actions?.userManagement?.canCreateProfile;
    const canEditProfile = user?.role?.profile?.permissions?.actions?.userManagement?.canEditProfile;
    const canDeleteProfile = user?.role?.profile?.permissions?.actions?.userManagement?.canDeleteProfile;

    const fetchProfiles = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await adminService.getProfiles();
            if (data.success) setProfiles(data.data);
        } catch (error) {
            console.error('Fallo al cargar perfiles:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProfiles();
    }, [fetchProfiles]);

    const handleCreate = () => {
        setDeleteError(null); // --- AJUSTE: Limpiar error al realizar otra acción
        setEditingProfile(null);
        setIsModalOpen(true);
    };

    const handleEdit = (profile) => {
        setDeleteError(null); // --- AJUSTE: Limpiar error al realizar otra acción
        setEditingProfile(profile);
        setIsModalOpen(true);
    };

    const handleDelete = async (profileId) => {
        setDeleteError(null); // Limpiar errores anteriores
        try {
            const response = await adminService.deleteProfile(profileId);
            if (response.data.success) {
                message.success('Perfil eliminado correctamente.');
                fetchProfiles();
            }
        } catch (error) {
            // --- AJUSTE: Se establece el error en el estado para mostrar la Alerta
            const errorMessage = error.response?.data?.message || 'No se pudo eliminar el perfil.';
            setDeleteError(errorMessage);
            console.error('Fallo al eliminar perfil:', error);
        }
    };

    const handleFormFinish = async (values) => {
        setIsSubmitting(true);
        try {
            if (editingProfile) {
                await adminService.updateProfile(editingProfile._id, values);
                message.success('Perfil actualizado correctamente.');
            } else {
                await adminService.createProfile(values);
                message.success('Perfil creado correctamente.');
            }
            setIsModalOpen(false);
            fetchProfiles();
        } catch (error) {
            console.error('Fallo en el formulario de perfil:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const columns = [
        { title: 'Nombre del Perfil', dataIndex: 'name', key: 'name' },
        {
            title: 'Acciones',
            key: 'actions',
            render: (_, record) => (
                <Space size="middle">
                    {canEditProfile && (
                        <Tooltip title="Editar"><Button type="primary" icon={<EditOutlined />} onClick={() => handleEdit(record)} /></Tooltip>
                    )}
                    {canDeleteProfile && (
                        <Tooltip title="Eliminar"><Popconfirm title="¿Está seguro de eliminar este perfil?" onConfirm={() => handleDelete(record._id)} okText="Sí" cancelText="No"><Button type="primary" danger icon={<DeleteOutlined />} /></Popconfirm></Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div>
            {/* --- AJUSTE: Se renderiza la Alerta si existe un error --- */}
            {deleteError && (
                <Alert
                    message="Error de Eliminación"
                    description={deleteError}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setDeleteError(null)}
                    style={{ marginBottom: 16 }}
                />
            )}
            {canCreateProfile && (
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} style={{ marginBottom: 16 }}>
                    Crear Perfil
                </Button>
            )}
            <Table columns={columns} dataSource={profiles} loading={loading} rowKey="_id" bordered />
            <ProfileForm
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onFinish={handleFormFinish}
                initialValues={editingProfile}
                submitting={isSubmitting}
            />
        </div>
    );
};

export default ProfilesTab;
